import type { ExpenseDraft, Movement } from "./types";

export const MIN_INSTALLMENT_COUNT = 2;
export const MAX_INSTALLMENT_COUNT = 24;

export interface InstallmentSlice {
  number: number;
  count: number;
  dueOn: string;
  billedCents: number;
  reimbursementCents: number;
  ownCents: number;
}

function validIsoDate(value: string): { year: number; month: number; day: number } | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day, 12);
  return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day
    ? { year, month, day }
    : null;
}

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Conserva el día de compra y usa el último día cuando el mes es más corto. */
export function addMonthsClamped(value: string, months: number): string | undefined {
  const parsed = validIsoDate(value);
  if (!parsed || !Number.isInteger(months) || months < 0) return undefined;
  const firstOfTarget = new Date(parsed.year, parsed.month + months, 1, 12);
  const lastDay = new Date(firstOfTarget.getFullYear(), firstOfTarget.getMonth() + 1, 0, 12).getDate();
  return isoDate(firstOfTarget.getFullYear(), firstOfTarget.getMonth(), Math.min(parsed.day, lastDay));
}

/** Reparte todos los centavos; las primeras parcialidades absorben el residuo. */
export function splitInstallmentCents(totalCents: number, count: number): number[] {
  if (!Number.isSafeInteger(totalCents)
    || totalCents < 0
    || !Number.isInteger(count)
    || count < MIN_INSTALLMENT_COUNT
    || count > MAX_INSTALLMENT_COUNT) return [];
  const base = Math.floor(totalCents / count);
  const remainder = totalCents % count;
  return Array.from({ length: count }, (_, index) => base + (index < remainder ? 1 : 0));
}

export function isInstallmentDraft(draft: Pick<ExpenseDraft, "installmentCount">): boolean {
  return Number.isInteger(draft.installmentCount) && Number(draft.installmentCount) >= MIN_INSTALLMENT_COUNT;
}

export function installmentSchedule(draft: ExpenseDraft): InstallmentSlice[] {
  if (!isInstallmentDraft(draft) || !draft.spentOn) return [];
  const count = Number(draft.installmentCount);
  const billed = splitInstallmentCents(draft.amountCents, count);
  const reimbursement = draft.reimbursementCents
    ? splitInstallmentCents(draft.reimbursementCents, count)
    : Array.from({ length: count }, () => 0);
  if (billed.length !== count || reimbursement.length !== count || billed.some((amount) => amount <= 0)) return [];
  const schedule = billed.map((billedCents, index) => {
    const dueOn = addMonthsClamped(draft.spentOn as string, index);
    const reimbursementCents = reimbursement[index] ?? 0;
    if (!dueOn || reimbursementCents > billedCents) return null;
    return {
      number: index + 1,
      count,
      dueOn,
      billedCents,
      reimbursementCents,
      ownCents: billedCents - reimbursementCents,
    } satisfies InstallmentSlice;
  });
  return schedule.every((item): item is InstallmentSlice => item !== null) ? schedule : [];
}

export function firstInstallmentSlice(draft: ExpenseDraft): InstallmentSlice | undefined {
  return installmentSchedule(draft)[0];
}

export function firstInstallmentDraft(draft: ExpenseDraft): ExpenseDraft | undefined {
  const first = firstInstallmentSlice(draft);
  if (!first) return undefined;
  return {
    ...draft,
    amountCents: first.billedCents,
    reimbursementCents: first.reimbursementCents,
    savingsContributionCents: 0,
    installmentCount: undefined,
    spentOn: first.dueOn,
  };
}

interface ScheduledMovementOptions {
  draft: ExpenseDraft;
  purchaseId: string;
  movementIds: string[];
  purchasePeriodId: string;
  createdAt: string;
  syncState: Movement["syncState"];
  periodForDate: (spentOn: string) => string | undefined;
}

/** Crea sólo las parcialidades futuras; la primera la aplica finance.ts a la quincena vigente. */
export function scheduledInstallmentMovements(options: ScheduledMovementOptions): Movement[] {
  const schedule = installmentSchedule(options.draft);
  if (schedule.length < MIN_INSTALLMENT_COUNT || options.movementIds.length !== schedule.length) return [];
  return schedule.slice(1).map((slice, index) => {
    const coveredAmountCents = options.draft.reimbursementReceived ? slice.reimbursementCents : 0;
    const pendingAmountCents = Math.max(0, slice.billedCents - coveredAmountCents);
    return {
      id: options.movementIds[index + 1],
      periodId: "MSI programado",
      kind: "expense",
      amountCents: slice.ownCents,
      billedAmountCents: slice.billedCents,
      merchant: options.draft.merchant || "Compra a MSI",
      description: options.draft.merchant || "Compra a MSI",
      paymentMethod: options.draft.paymentMethod,
      category: options.draft.category,
      reimbursementCents: slice.reimbursementCents,
      reimbursementReceived: options.draft.reimbursementReceived,
      budgetImpact: options.draft.budgetImpact,
      cardFundingMode: "later",
      amountTodayCents: 0,
      weeklyImpactCents: 0,
      accountDeltaCents: 0,
      paidNow: pendingAmountCents === 0,
      coveredAmountCents,
      pendingAmountCents,
      cardStatus: pendingAmountCents > 0 ? "Programada" : "Cubierta",
      spentOn: slice.dueOn,
      cardPeriod: options.periodForDate(slice.dueOn),
      sharedExpense: options.draft.sharedExpense,
      installmentPurchaseId: options.purchaseId,
      installmentNumber: slice.number,
      installmentCount: slice.count,
      installmentTotalCents: options.draft.amountCents,
      installmentTotalReimbursementCents: options.draft.reimbursementCents ?? 0,
      installmentDueOn: slice.dueOn,
      installmentPurchaseDate: options.draft.spentOn,
      installmentRecognizedCents: 0,
      installmentPurchasePeriodId: options.purchasePeriodId,
      origin: "app",
      schemaVersion: 3,
      createdAt: options.createdAt,
      syncState: options.syncState,
    } satisfies Movement;
  });
}

type InstallmentAvailability = Pick<Movement, "installmentDueOn" | "installmentPurchaseId" | "cardPeriod">;

/** El corte persistido gobierna el fondeo; las fechas originales no se reescriben. */
export function installmentAvailableFrom(item: InstallmentAvailability): string | undefined {
  if (!item.installmentPurchaseId) return undefined;
  const bounds = item.cardPeriod?.trim().match(/^(\d{4}-\d{2}-\d{2}) al (\d{4}-\d{2}-\d{2})$/);
  if (bounds && validIsoDate(bounds[1]) && validIsoDate(bounds[2]) && bounds[1] <= bounds[2]) return bounds[1];
  // Los registros sin corte válido conservan su fecha mensual como respaldo.
  return item.installmentDueOn && validIsoDate(item.installmentDueOn) ? item.installmentDueOn : undefined;
}

export function installmentIsDue(
  item: InstallmentAvailability,
  today: string,
): boolean {
  if (!item.installmentPurchaseId) return true;
  const availableFrom = installmentAvailableFrom(item);
  return Boolean(availableFrom && validIsoDate(today) && availableFrom <= today);
}
